/**
 * useMigrateBranding — the two-step "Migrate Branding to scio3" flow.
 *
 * Verification is a LAZY QUERY (pure read, safe to re-run against prod) and the
 * migration is a separate mutation, so the operator always sees the current state
 * before committing. Re-running the preflight after a migration doubles as the
 * progress check: done when `cfProvisionState` and `cfSslStatus` both read
 * `active`.
 *
 * Design: claude-dev/feature-paullai/2026-08-04-migrate-branding-scio3/02-plan.md
 */
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  STAFF_BRANDING_MIGRATION_PREFLIGHT,
  STAFF_MIGRATE_BRANDING_TO_SCIO3,
} from '@staff-app/graphql-client';
import type { SalesActionResult } from './useSalesConsole';

/** 'vanity' = real customer domain · 'subhost' = `<sub>.<zone>` · 'none' = no record. */
export type BrandingDomainType = 'vanity' | 'subhost' | 'none';

/**
 * One preflight condition. `blocking: false` means a failure is informational —
 * today only the add-on check, which the migration grants rather than requires.
 */
export interface BrandingMigrationCheck {
  key: string;
  label: string;
  ok: boolean;
  blocking: boolean;
  actual?: string | null;
  detail?: string | null;
}

export interface BrandingMigrationPreflight {
  accountId: string;
  companyName?: string | null;
  host?: string | null;
  subHost?: string | null;
  version?: number | null;
  cfProvisionState?: string | null;
  cfSslStatus?: string | null;
  planFeature?: string | null;
  domainType: BrandingDomainType;
  canMigrate: boolean;
  willGrantAddOn: boolean;
  checks: BrandingMigrationCheck[];
}

type StripeVars = { stripeId: string };

export function useBrandingMigrationPreflight() {
  return useLazyQuery<
    { staffBrandingMigrationPreflight: BrandingMigrationPreflight },
    StripeVars
  >(STAFF_BRANDING_MIGRATION_PREFLIGHT, {
    // Always hit the server: the operator re-runs this to watch provisioning
    // progress, so a cached result would look like a stalled migration.
    fetchPolicy: 'network-only',
  });
}

export function useMigrateBrandingToScio3() {
  return useMutation<{ staffMigrateBrandingToScio3: SalesActionResult }, StripeVars>(
    STAFF_MIGRATE_BRANDING_TO_SCIO3,
  );
}
